import { extendTheme } from "native-base";

import { WhooshTheme } from "@whoosh/design-system/src";

interface iWhooshThemeBase {
  palette: {
    neutrals: {
      black: string;
      _20: string;
      _30: string;
      _50: string;
      _55: string;
      _60: string;
      _65: string;
      _70: string;
      _75: string;
      _80: string;
      white: string;
      transparent: string;
    };
    ui: {
      primary: iWhooshColor;
      secondary: iWhooshColor;
      member: iWhooshColor;
      guest: iWhooshColor;
    };
    semantic: {
      success: iWhooshColor;
      warning: iWhooshColor;
      error: iWhooshColor;
      info: iWhooshColor;
    };
    events: {
      blue: string;
      blueLight: string;
      purple: string;
      purpleLight: string;
      red: string;
      redLight: string;
      green: string;
      greenLight: string;
      orange: string;
      orangeLight: string;
    };
  };
  typography: {
    mainFont: string;
    secondaryFont: string;
  };
}

/**
 * This represents a small shade list of colors.
 * our base color can be considered _50 generally, with
 * lower numbers representing darker colors, and higher numbers representing lighter colors,
 * however - that is somewhat arbitrary as for example a 'dark mode' would flip the order.
 * this also leaves us open to extend to additional shades if necessary
 */
interface iWhooshColor {
  _20: string;
  base: string;
  _40: string;
  _50: string;
  _55: string;
  _60: string;
  _70: string;
  _80: string;
  _85: string;
}

/**
 * TODO: Eventually this can be rolled into a function that will allow theme selection/switching
 * providing for easy addition of things like "dark mode" or club colors
 * The idea here is that by typing this, we can segment important ui colors that shouldn't change (ie: alert colors)
 * and arbitrary branding colors that are alright to change.
 */
export const whooshThemeBase: iWhooshThemeBase = {
  palette: {
    neutrals: {
      black: "#000000",
      _20: "#002726",
      _30: "#405D5C",
      _50: "#809392",
      _55: "#B8BFBF",
      _60: "#DBDBDB",
      _65: "#E5E5E5",
      _70: "#EBEBEB", //Design uses as dividers but doesn't appear in styleguide.
      _75: "#F2F2F2",
      _80: "#F7F7F7",
      white: "#FFFFFF",
      transparent: "transparent",
    },
    ui: {
      primary: {
        base: "#1DAA75",
        _20: "#002726",
        _40: "#295F3E",
        _50: "#67877B",
        _55: "#00A887",
        _60: "#41775B",
        _70: "#ACE5CF",
        _80: "#E1F5ED",
        _85: "#124734",
      },
      secondary: {
        base: "#A8A087",
        _20: "#3B372B",
        _40: "#3B372B",
        _50: "#3B372B",
        _55: "#3B372B",
        _60: "#3B372B",
        _70: "#9FBED7",
        _80: "#E5E3DC",
        _85: "#E5E3DC",
      },
      member: {
        base: "#1DAA75",
        _20: "#002726",
        _40: "#295F3E",
        _50: "#67877B",
        _55: "#00A887",
        _60: "#41775B",
        _70: "#ACE5CF",
        _80: "#E1F5ED",
        _85: "#124734",
      },
      guest: {
        base: "#FF784B",
        _20: "#271000",
        _40: "#271000",
        _50: "#271000",
        _55: "#271000",
        _60: "#271000",
        _70: "#FFDDD1",
        _80: "#FFECE5",
        _85: "#E5E3DC",
      },
    },
    semantic: {
      success: {
        base: "#1DAA75",
        _20: "#188F62",
        _40: "#188F62",
        _50: "#188F62",
        _55: "#188F62",
        _60: "#188F62",
        _70: "#ACE5CF",
        _80: "#E1F5ED",
        _85: "#E1F5ED",
      },
      warning: {
        base: "#E58B04",
        _20: "#E58B04",
        _40: "#E58B04",
        _50: "#E58B04",
        _55: "#E58B04",
        _60: "#E58B04",
        _70: "#FFE5B2",
        _80: "#FFE5B2",
        _85: "#FFE5B2",
      },
      error: {
        base: "#FF5959",
        _20: "#D64343",
        _40: "#D64343",
        _50: "#D64343",
        _55: "#D64343",
        _60: "#D64343",
        _70: "#FF5959",
        _80: "#FFE6E6",
        _85: "#FFE6E6",
      },
      info: {
        base: "#809392",
        _20: "#405D5C",
        _40: "#405D5C",
        _50: "#405D5C",
        _55: "#405D5C",
        _60: "#405D5C",
        _70: "#809392",
        _80: "#809392",
        _85: "#809392",
      },
    },
    events: {
      blue: "#5C8BCC",
      blueLight: "#DFE8F2",
      purple: "#8C81D6",
      purpleLight: "#EDEBFA",
      red: "#CC7A8F",
      redLight: "#FAE6EB",
      green: "#70CCA6",
      greenLight: "#E1F5ED",
      orange: "#E5A88A",
      orangeLight: "#FFECE5",
    },
  },
  typography: {
    mainFont: "'Inter', sans-serif",
    //secondaryFont: "'Calluna', serif",
    secondaryFont: "'Tinos', serif",
  },
};

export enum DesignSize {
  SM = "sm",
  MD = "md",
  LG = "lg",
}

export enum WhooshVariants {
  PRIMARY = "primary",
  SECONDARY = "secondary",
  DANGER = "danger",
  AFFIRM = "affirm",
  MEMBER = "member",
  GUEST = "guest",
  DIM = "dim",
  FLAT = "flat",
  BLACK = "black",
  TERTIARY = "tertiary",
}

export interface VariantStyles {
  backgroundColor: string;
  borderColor: string;
  color: string;
}

/**
 * Broke out the ole variants so we can share styles more easily for them, as well as re-theme them more easily
 */
type AllWhooshVariantStyles = { [key in WhooshVariants]: VariantStyles };
interface iWhooshVariantStyles {
  variantStyles: AllWhooshVariantStyles;
}

const whooshThemeVariants: iWhooshVariantStyles = {
  variantStyles: {
    [WhooshVariants.PRIMARY]: {
      backgroundColor: whooshThemeBase.palette.ui.primary.base,
      color: whooshThemeBase.palette.neutrals.white,
      borderColor: whooshThemeBase.palette.neutrals._70,
    },
    [WhooshVariants.SECONDARY]: {
      backgroundColor: whooshThemeBase.palette.neutrals.white,
      color: whooshThemeBase.palette.ui.primary._20,
      borderColor: whooshThemeBase.palette.neutrals._60,
    },
    [WhooshVariants.MEMBER]: {
      backgroundColor: whooshThemeBase.palette.ui.member._70,
      color: whooshThemeBase.palette.ui.member._20,
      borderColor: whooshThemeBase.palette.neutrals._70,
    },
    [WhooshVariants.DANGER]: {
      backgroundColor: whooshThemeBase.palette.ui.guest._70,
      color: whooshThemeBase.palette.semantic.error.base,
      borderColor: whooshThemeBase.palette.neutrals._70,
    },
    [WhooshVariants.GUEST]: {
      backgroundColor: whooshThemeBase.palette.ui.guest._70,
      color: whooshThemeBase.palette.ui.guest._20,
      borderColor: whooshThemeBase.palette.neutrals._70,
    },
    [WhooshVariants.DIM]: {
      backgroundColor: whooshThemeBase.palette.neutrals._80,
      color: whooshThemeBase.palette.ui.primary._20,
      borderColor: whooshThemeBase.palette.ui.primary._20,
    },
    [WhooshVariants.FLAT]: {
      backgroundColor: whooshThemeBase.palette.neutrals.transparent,
      color: whooshThemeBase.palette.ui.primary._20,
      borderColor: whooshThemeBase.palette.neutrals.transparent,
    },
    [WhooshVariants.BLACK]: {
      backgroundColor: whooshThemeBase.palette.neutrals.black,
      color: whooshThemeBase.palette.neutrals.white,
      borderColor: whooshThemeBase.palette.neutrals.black,
    },
    [WhooshVariants.AFFIRM]: {
      backgroundColor: whooshThemeBase.palette.semantic.success._80,
      color: whooshThemeBase.palette.semantic.success.base,
      borderColor: whooshThemeBase.palette.neutrals._70,
    },
    [WhooshVariants.TERTIARY]: {
      backgroundColor: whooshThemeBase.palette.neutrals.white,
      color: whooshThemeBase.palette.ui.primary._20,
      borderColor: whooshThemeBase.palette.neutrals._65,
    },
  },
};
export interface iWhooshTheme extends iWhooshThemeBase, iWhooshVariantStyles {}
export const whooshTheme: iWhooshTheme = {
  ...whooshThemeBase,
  ...whooshThemeVariants,
};

export const WhooshNBStaffTheme = extendTheme({
  ...WhooshTheme,
  fontConfig: {
    Calluna: {
      100: {
        normal: "'Tinos'",
        bold: "'Tinos'",
      },
      200: {
        normal: "'Tinos'",
        bold: "'Tinos'",
      },
      300: {
        normal: "'Tinos'",
        bold: "'Tinos'",
      },
      400: {
        normal: "'Tinos'",
        bold: "'Tinos'",
      },
      500: {
        normal: "'Tinos'",
        bold: "'Tinos'",
      },
      600: {
        normal: "'Tinos'",
        bold: "'Tinos'",
      },
      700: {
        normal: "'Tinos'",
        bold: "'Tinos'",
      },
      800: {
        normal: "'Tinos'",
        bold: "'Tinos'",
      },
      900: {
        normal: "'Tinos'",
        bold: "'Tinos'",
      },
    },
    Inter: {
      100: {
        normal: "Inter",
      },
      200: {
        normal: "Inter",
      },
      300: {
        normal: "Inter",
      },
      400: {
        normal: "Inter",
      },
      500: {
        normal: "Inter",
      },
      600: {
        normal: "Inter",
      },
      700: {
        normal: "Inter",
      },
      800: {
        normal: "Inter",
      },
    },
  },
  fonts: {
    heading: "Tinos",
    body: "Inter",
    mono: "Inter",
  },
  components: {
    ...WhooshTheme.components,
    Heading: {
      defaultProps: {
        letterSpacing: -0.2,
        lineHeight: "28px",
        fontWeight: "bold",
        fontVariant: ["lining-nums", "tabular-nums"],
        fontFamily: "heading",
      },
      baseStyle: {
        color: "neutrals.200",
      },
    },
  },
});
